import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  TrendingUp, TrendingDown, DollarSign, Clock, 
  Loader2, Award, CheckCircle, CreditCard, Users,
  Filter, Calendar, Briefcase
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const BonusDashboardPage = () => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [selectedBonuses, setSelectedBonuses] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, [filterPeriod]);

  const fetchDashboard = async () => {
    try {
      const params = {};
      if (filterPeriod !== 'all') params.period = filterPeriod;
      
      const response = await api.get('/ratings/bonuses/dashboard', { params });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching bonus dashboard:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBonus = (bonusId) => {
    setSelectedBonuses(prev => 
      prev.includes(bonusId) 
        ? prev.filter(id => id !== bonusId)
        : [...prev, bonusId]
    );
  };

  const handleSelectAll = () => {
    const pendingBonuses = filteredBonuses.filter(b => b.status === 'pending').map(b => b.id);
    if (selectedBonuses.length === pendingBonuses.length) {
      setSelectedBonuses([]);
    } else {
      setSelectedBonuses(pendingBonuses);
    }
  };

  const handleBatchApprove = async () => {
    if (selectedBonuses.length === 0) return;
    
    setProcessing(true);
    try {
      await api.post('/ratings/bonuses/batch-approve', selectedBonuses);
      toast.success(`${selectedBonuses.length} prime(s) approuvée(s)`);
      setSelectedBonuses([]);
      fetchDashboard();
    } catch (error) {
      toast.error('Erreur lors de l\'approbation');
    } finally {
      setProcessing(false);
    }
  };

  const handleBatchMarkPaid = async () => {
    const approvedSelected = selectedBonuses.filter(id => {
      const bonus = dashboard?.recent_bonuses?.find(b => b.id === id);
      return bonus?.status === 'approved';
    });
    
    if (approvedSelected.length === 0) {
      toast.error('Sélectionnez des primes approuvées');
      return;
    }
    
    setProcessing(true);
    try {
      await api.post('/ratings/bonuses/batch-mark-paid', approvedSelected);
      toast.success(`${approvedSelected.length} prime(s) marquée(s) comme payée(s)`);
      setSelectedBonuses([]);
      fetchDashboard();
    } catch (error) {
      toast.error('Erreur lors du marquage');
    } finally {
      setProcessing(false);
    }
  };

  const filteredBonuses = (dashboard?.recent_bonuses || []).filter(b => {
    if (filterStatus === 'all') return true;
    return b.status === filterStatus;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return { label: 'En attente', color: 'bg-amber-100 text-amber-700' };
      case 'approved':
        return { label: 'Approuvé', color: 'bg-blue-100 text-blue-700' };
      case 'paid':
        return { label: 'Payé', color: 'bg-emerald-100 text-emerald-700' };
      default:
        return { label: status, color: 'bg-slate-100 text-slate-700' };
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </MainLayout>
    );
  }

  const summary = dashboard?.summary || {};
  const byMember = dashboard?.by_member || [];

  return (
    <MainLayout>
      <div className="p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Gestion des Bonus/Malus
            </h1>
            <p className="text-slate-500 mt-1">
              Gérez les primes de performance des membres
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-emerald-600">+{summary.total_bonus?.toFixed(2) || 0}€</p>
                  <p className="text-xs text-slate-500">Total Bonus</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-red-600">-{summary.total_malus?.toFixed(2) || 0}€</p>
                  <p className="text-xs text-slate-500">Total Malus</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className={cn(
                    "text-xl font-bold",
                    (summary.net_amount || 0) >= 0 ? "text-emerald-600" : "text-red-600"
                  )}>
                    {(summary.net_amount || 0) >= 0 ? '+' : ''}{summary.net_amount?.toFixed(2) || 0}€
                  </p>
                  <p className="text-xs text-slate-500">Solde Net</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-amber-600">{summary.pending_amount?.toFixed(2) || 0}€</p>
                  <p className="text-xs text-slate-500">En attente</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-purple-600">{summary.approved_amount?.toFixed(2) || 0}€</p>
                  <p className="text-xs text-slate-500">Approuvés</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-xl font-bold">{summary.paid_amount?.toFixed(2) || 0}€</p>
                  <p className="text-xs text-slate-500">Payés</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Member Summary */}
        {byMember.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Résumé par membre
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {byMember.map((member, idx) => (
                  <div key={idx} className="p-4 border rounded-xl hover:bg-slate-50 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{member.member_name}</span>
                      <Badge variant="outline">{member.project_count} projets</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-emerald-600">+{member.total_bonus?.toFixed(2)}€</span>
                      <span className="text-red-600">-{member.total_malus?.toFixed(2)}€</span>
                      {member.pending > 0 && (
                        <span className="text-amber-600">({member.pending.toFixed(2)}€ en attente)</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters and Actions */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex flex-col md:flex-row gap-4">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-full md:w-[180px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="approved">Approuvés</SelectItem>
                    <SelectItem value="paid">Payés</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                  <SelectTrigger className="w-full md:w-[180px]">
                    <Calendar className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Période" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toute la période</SelectItem>
                    <SelectItem value="current_month">Ce mois</SelectItem>
                    <SelectItem value="last_month">Mois dernier</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {selectedBonuses.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">{selectedBonuses.length} sélectionné(s)</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBatchApprove}
                    disabled={processing}
                    className="gap-1"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Approuver
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleBatchMarkPaid}
                    disabled={processing}
                    className="gap-1"
                    style={{ backgroundColor: theme.colors?.accent }}
                  >
                    <CreditCard className="w-4 h-4" />
                    Marquer payé
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bonuses List */}
        {filteredBonuses.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Award className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucune prime</h3>
              <p className="text-slate-500">
                Les primes seront créées après l&apos;évaluation des projets
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="p-4 text-left">
                      <Checkbox 
                        checked={selectedBonuses.length === filteredBonuses.filter(b => b.status === 'pending').length && filteredBonuses.filter(b => b.status === 'pending').length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                    </th>
                    <th className="p-4 text-left text-sm font-medium text-slate-700">Projet</th>
                    <th className="p-4 text-left text-sm font-medium text-slate-700">Membre</th>
                    <th className="p-4 text-left text-sm font-medium text-slate-700">Type</th>
                    <th className="p-4 text-left text-sm font-medium text-slate-700">Note</th>
                    <th className="p-4 text-right text-sm font-medium text-slate-700">Montant</th>
                    <th className="p-4 text-center text-sm font-medium text-slate-700">Statut</th>
                    <th className="p-4 text-left text-sm font-medium text-slate-700">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBonuses.map((bonus, idx) => {
                    const statusBadge = getStatusBadge(bonus.status);
                    return (
                      <tr key={idx} className="border-b hover:bg-slate-50">
                        <td className="p-4">
                          {bonus.status === 'pending' && (
                            <Checkbox 
                              checked={selectedBonuses.includes(bonus.id)}
                              onCheckedChange={() => handleSelectBonus(bonus.id)}
                            />
                          )}
                        </td>
                        <td className="p-4">
                          <span className="font-medium">{bonus.project_name}</span>
                        </td>
                        <td className="p-4 text-slate-600">{bonus.member_name}</td>
                        <td className="p-4">
                          <Badge className={bonus.bonus_type === 'bonus' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                            {bonus.bonus_type === 'bonus' ? (
                              <><TrendingUp className="w-3 h-3 mr-1" />Bonus</>
                            ) : (
                              <><TrendingDown className="w-3 h-3 mr-1" />Malus</>
                            )}
                          </Badge>
                        </td>
                        <td className="p-4">{bonus.rating}/20</td>
                        <td className="p-4 text-right">
                          <span className={cn(
                            "font-bold",
                            bonus.bonus_amount > 0 ? "text-emerald-600" : "text-red-600"
                          )}>
                            {bonus.bonus_amount > 0 ? '+' : ''}{bonus.bonus_amount?.toFixed(2)}€
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          <Badge className={statusBadge.color}>{statusBadge.label}</Badge>
                        </td>
                        <td className="p-4 text-sm text-slate-500">
                          {new Date(bonus.created_at).toLocaleDateString('fr-FR')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default BonusDashboardPage;
